import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Mail, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "./ui";
import { api } from "../lib/api";
import { useTranslation } from "react-i18next";

/**
 * Per-account control over what gets delivered and where.
 *
 * The server enforces this in `notify()`, which every producer in the product goes
 * through — so switching email off here genuinely stops the email rather than
 * hiding a row that was written anyway.
 *
 * Transactional messages (billing, security) are deliberately absent: those are not
 * marketing and are always delivered, so offering a switch would be a lie.
 */
const KIND_ICONS = { alert: "🔔", comment: "💬", comment_moderated: "✅",
                     new_position: "📈", new_post: "📝", trial_ending: "⏳",
                     message: "✉️" };

export default function NotificationPrefs() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.notificationPrefs()
      .then((r) => { setPrefs(r.data.preferences); setKinds(r.data.kinds || []); })
      .catch(() => {});
  }, []);

  const save = async (next) => {
    const previous = prefs;
    setPrefs(next);            // optimistic: the switch should feel instant
    setBusy(true);
    try {
      const { data } = await api.setNotificationPrefs(next);
      setPrefs(data.preferences);
    } catch (e) {
      setPrefs(previous);      // put it back rather than lying about what is stored
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
    } finally { setBusy(false); }
  };

  if (!prefs) {
    return <Panel title={t("notifyPrefs.title")}><div className="h-24 animate-pulse bg-slate-50 rounded-xl" /></Panel>;
  }

  const setChannel = (c, v) => save({ ...prefs, channels: { ...prefs.channels, [c]: v } });
  const setKind = (k, v) => save({ ...prefs, kinds: { ...prefs.kinds, [k]: v } });
  const muted = prefs.muted_all;

  return (
    <Panel title={t("notifyPrefs.title")} data-testid="notify-prefs">
      <p className="text-sm text-slate-500 -mt-1">{t("notifyPrefs.body")}</p>

      <button
        type="button"
        data-testid="notify-mute-all"
        onClick={() => save({ ...prefs, muted_all: !muted })}
        className={`mt-4 w-full flex items-center gap-3 rounded-xl px-3.5 py-3 text-left transition-colors ${
          muted ? "bg-amber-50 ring-1 ring-amber-300" : "bg-slate-50 hover:bg-slate-100"
        }`}
      >
        {muted ? <BellOff size={16} className="text-amber-600 shrink-0" />
               : <Bell size={16} className="text-slate-400 shrink-0" />}
        <span className="min-w-0">
          <span className="block text-sm font-medium text-slate-800">
            {muted ? t("notifyPrefs.mutedTitle") : t("notifyPrefs.muteTitle")}
          </span>
          <span className="block text-xs text-slate-500">
            {muted ? t("notifyPrefs.mutedHint") : t("notifyPrefs.muteHint")}
          </span>
        </span>
        {busy && <Loader2 size={14} className="animate-spin text-slate-400 ml-auto shrink-0" />}
      </button>

      <div className={muted ? "opacity-40 pointer-events-none" : ""}>
        <div className="mt-5 text-xs font-semibold uppercase tracking-wide text-slate-400">
          {t("notifyPrefs.channels")}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {[["inapp", Smartphone], ["email", Mail]].map(([c, Icon]) => (
            <button
              key={c}
              type="button"
              data-testid={`notify-channel-${c}`}
              onClick={() => setChannel(c, !prefs.channels[c])}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors ${
                prefs.channels[c]
                  ? "border-brand-300 bg-brand-50 text-brand-700 font-medium"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50"
              }`}
            >
              <Icon size={15} className="shrink-0" /> {t(`notifyPrefs.channel_${c}`)}
            </button>
          ))}
        </div>

        <div className="mt-5 text-xs font-semibold uppercase tracking-wide text-slate-400">
          {t("notifyPrefs.kinds")}
        </div>
        <div className="mt-2 space-y-1.5">
          {kinds.map((k) => (
            <label key={k} className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
              <span className="text-sm text-slate-700 min-w-0">
                <span className="mr-1.5" aria-hidden="true">{KIND_ICONS[k] || "•"}</span>
                {t(`notifyPrefs.kind_${k}`)}
              </span>
              <input
                type="checkbox"
                data-testid={`notify-kind-${k}`}
                checked={!!prefs.kinds[k]}
                onChange={(e) => setKind(k, e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-500 shrink-0"
              />
            </label>
          ))}
        </div>
      </div>
    </Panel>
  );
}
